<template>
  <validation-observer ref="observer">
    <confirmation-dialog
      v-if="deleteFilterConfirmation"
      :visible="deleteFilterConfirmation"
      :message="$t('message.confirm_delete_filter')"
      @confirm="deleteFilter"
      @close="deleteFilterConfirmation = false"
    />
    <modal-dialog
      :visible="true"
      persistent
      scrollable
      max-width="800px"
      card-class="pa-5"
      content-class="dialog-height-80"
      container-class="pa-0"
      :title="$t('label.contacts_filter')"
    >
      <v-row>
        <v-col
          cols="12"
          class="pt-4 pb-0"
        >
          <validated-text-field
            v-model="cf.name"
            autofocus
            rules="required|min:2|max:254"
            :label="$t('label.contact_filter_name')"
          />
        </v-col>

        <row-form-divider class="py-1" />

        <v-col
          cols="12"
          class="pb-5"
        >
          <span class="text-subtitle-2 text--primary font-weight-bold">
            {{ $t('label.filter_define_selection') }}
          </span>
          {{ $t('label.filter_define_selection_hint') }}:
        </v-col>
        <slot name="define-rules" />
        <v-col
          v-for="(rule, i) in defineRules"
          :key="`define_${rule.id}`"
          class="py-0"
          cols="12"
        >
          <validation-provider
            v-slot="{ errors }"
            :rules="editValidation ? ({cfrownoop: { descriptor }}) : ({cfrow: { descriptor }})"
          >
            <contacts-filter-value-row
              v-model="defineRules[i]"
              :descriptor="descriptor"
              :last-row="i===defineRules.length-1"
              :error-messages="errors"
              @add="addRule(i, defineRules)"
              @remove="removeRule(i, defineRules)"
              @input="ruleModified"
            />
          </validation-provider>
        </v-col>

        <template v-if="hasAdvancedRule || advancedVisibleToggle">
          <row-form-divider class="pt-5 pb-2" />

          <v-col
            cols="12"
            class="pb-5"
          >
            <span class="text-subtitle-2 text--primary font-weight-bold">
              {{ $t('label.filter_narrow_down_selection') }}
            </span>
            {{ $t('label.filter_narrow_down_selection_hint') }}:
          </v-col>
          <slot name="narrow-rules" />
          <v-col
            v-for="(rule, i) in narrowRules"
            :key="`narrow_${rule.id}`"
            cols="12"
            class="py-0"
          >
            <validation-provider
              v-slot="{ errors }"
              :rules="editValidation ? ({cfrownoop: { descriptor }}) : ({cfrow: { descriptor }})"
            >
              <contacts-filter-value-row
                v-model="narrowRules[i]"
                :descriptor="descriptor"
                :last-row="i===narrowRules.length-1"
                :error-messages="errors"
                @add="addRule(i, narrowRules)"
                @remove="removeRule(i, narrowRules)"
                @input="ruleModified"
              />
            </validation-provider>
          </v-col>

          <row-form-divider class="pt-5 pb-2" />

          <v-col
            cols="12"
            class="pb-5"
          >
            <span class="text-subtitle-2 text--primary font-weight-bold">
              {{ $t('label.filter_exclude_from_selection') }}
            </span>
            {{ $t('label.filter_exclude_from_selection_hint') }}:
          </v-col>

          <v-col
            v-for="(rule, i) in excludeRules"
            :key="`exclude_${rule.id}`"
            class="py-0"
            cols="12"
          >
            <validation-provider
              v-slot="{ errors }"
              :rules="editValidation ? ({cfrownoop: { descriptor }}) : ({cfrow: { descriptor }})"
            >
              <contacts-filter-value-row
                v-model="excludeRules[i]"
                :descriptor="descriptor"
                :last-row="i===excludeRules.length-1"
                :error-messages="errors"
                @add="addRule(i, excludeRules)"
                @remove="removeRule(i, excludeRules)"
                @input="ruleModified"
              />
            </validation-provider>
          </v-col>
        </template>

        <v-col
          cols="12"
          class="d-flex flex-row py-0 my-0 pt-4"
        >
          <v-spacer />
          <v-btn
            text
            outlined
            small
            :disabled="hasAdvancedRule"
            @click="advancedVisibleToggle=!advancedVisibleToggle"
          >
            {{ advancedVisibleToggle ? $t('button.hide_advanced_options') : $t('button.show_advanced_options') }}
            <v-icon small>
              {{ advancedVisibleToggle ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
            </v-icon>
          </v-btn>
          <v-spacer />
        </v-col>

        <row-form-divider class="pt-5 pb-2" />

        <v-col cols="12">
          <slot
            name="footer"
            :count="matchContactCount"
          />
          <span
            v-if="!$scopedSlots.footer && matchContactCount !== null"
            class="text-subtitle-2"
          >{{ $t('label.filter_matches_x_guests', [matchContactCount]) }}</span>
        </v-col>
      </v-row>

      <template #actions>
        <v-btn
          v-if="!isNewFilter"
          color="error"
          @click="deleteFilterConfirmation=true"
        >
          <v-icon>mdi-delete</v-icon>
          {{ $t('button.delete_filter') }}
        </v-btn>
        <v-spacer />
        <v-btn
          color="success"
          @click="save"
        >
          {{ $t('button.save') }}
        </v-btn>
        <v-btn
          text
          color="error"
          @click="close"
        >
          {{ $t('button.close') }}
        </v-btn>
      </template>
    </modal-dialog>
  </validation-observer>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import DialogClosable from '@/mixins/dialog-closable';
import ContactFilter from '@/grpc-api/model/contact-filter';
import { Filter } from '@/services/filter/filter';
import { cloneModel } from '@/model/model-utils';
import { FilterDescriptor } from '@/services/filter/filter-descriptor';
import { FilterConvert } from '@/services/filter/filter-convert';
import { FilterFieldRule, FilterRule } from '@/services/filter/filter-rule';
import { ValidationObserver } from 'vee-validate';
import ConfirmationDialog from '@/components/dialogs/ConfirmationDialog.vue';
import ContactsFilterValueRow from './ContactsFilterValueRow.vue';

export default Vue.extend({
  name: 'ContactsFilterDialog',
  components: { ContactsFilterValueRow, ConfirmationDialog },
  mixins: [DialogClosable],
  props: {
    contactFilter: { type: Object as PropType<ContactFilter | null>, required: false, default: null },
    matchContactCount: { type: Number as PropType<Number | null>, required: false, default: null },
  },
  data() {
    return {
      cf: new ContactFilter(),
      defineRules: [] as FilterFieldRule[],
      narrowRules: [] as FilterFieldRule[],
      excludeRules: [] as FilterFieldRule[],
      lastid: 0,
      // matchContactCount: null as number | null,
      deleteFilterConfirmation: false,
      editValidation: true,
      advancedVisibleToggle: false,
    };
  },
  computed: {
    hasAdvancedRule(): boolean {
      return this.narrowRules.filter((r) => r.fieldType !== 'none').length > 0
        || this.excludeRules.filter((r) => r.fieldType !== 'none').length > 0;
    },
    descriptor(): FilterDescriptor {
      return ContactFilter.FilterDescriptor;
    },
    filter(): Filter | null {
      const trim = (rs: FilterFieldRule[]) => rs.filter((r) => this.descriptor.validateRule(r) === null);

      const defineRules = trim(this.defineRules);
      const narrowRules = trim(this.narrowRules);
      const excludeRules = trim(this.excludeRules);
      const filter = Filter.newFilterFrom3Rules({ defineRules, narrowRules, excludeRules });
      return filter;
    },
    isNewFilter(): boolean {
      return this.cf.id === 0;
    },
  },
  watch: {
    filter(filter: Filter) {
      this.changed();
      // console.log('changed: ', this.filter, this.matchContactCount);
    },
  },
  mounted() {
    this.cf = this.contactFilter ? cloneModel(this.contactFilter) : new ContactFilter();

    let f = new Filter();
    if (this.cf.data) {
      try {
        f = FilterConvert.fromJson(this.cf.data);
      } catch (e) {
        console.log('Error loading filter: ', e);
      }
    }
    const rules = f.get3Rules();

    this.defineRules = [...rules.defineRules as FilterFieldRule[], new FilterFieldRule()];
    this.narrowRules = [...rules.narrowRules as FilterFieldRule[], new FilterFieldRule()];
    this.excludeRules = [...rules.excludeRules as FilterFieldRule[], new FilterFieldRule()];

    [...this.defineRules, ...this.narrowRules, ...this.excludeRules].forEach(
    // eslint-disable-next-line no-param-reassign
      (r) => { this.lastid += 1; r.id = this.lastid; },
    );

    this.advancedVisibleToggle = this.hasAdvancedRule;
  },
  methods: {
    close() {
      this.$emit('close');
    },
    addRule(index: number, rules: FilterRule[]) {
      const r = new FilterFieldRule();
      this.lastid += 1; r.id = this.lastid;
      rules.push(r);
    },
    removeRule(index: number, rules: FilterRule[]) {
      rules.splice(index, 1);
    },
    async save() {
      // enforce operand chek when save
      this.editValidation = false;
      await this.$nextTick();

      const observer = this.$refs.observer as InstanceType<typeof ValidationObserver>;
      const isValid = await observer.validate();

      // back to edit mode with no operand check
      // await this.$nextTick();
      // this.editValidation = true;

      if (!isValid) return;

      if (!this.filter) return;

      this.cf.data = FilterConvert.toJson(this.filter);

      this.$emit('save', this.cf);
    },
    changed() {
      if (!this.filter) return;

      this.cf.data = FilterConvert.toJson(this.filter);

      this.$emit('changed', this.cf);
    },
    async deleteFilter() {
      this.$emit('delete', this.cf);
    },
    ruleModified() {
      // back to edit mode with no operand check
      this.editValidation = true;
    },
  },
});
</script>
